import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchCities } from './api';
import { Box, Card, Typography, TextField, Button, Avatar, IconButton, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, useTheme, alpha, useMediaQuery } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import { useTranslation } from 'react-i18next';
import { syncCurrencyAfterAuth } from './currencyHelpers';
import config from './config';

const cloneNotificationByTypeForUser = async (type, userId) => {
  try {
    await axios.post(`${config.API_BASE_URL}/Notifications/clone-by-type-for-user`, { type, userId });
  } catch (e) {}
};

const Register = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  const [form, setForm] = useState({ fullName: '', email: '', password: '', phone: '', city: '', address: '' });
  const [cities, setCities] = useState([]);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);

  useEffect(() => {
    fetchCities()
      .then(setCities)
      .catch(() => setCities([]));
  }, []);
  
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [message, setMessage] = useState('');
  const [redirect, setRedirect] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'fullName') {
      formattedValue = value.toLowerCase();
    }

    if (name === 'phone') {
      formattedValue = value.replace(/[^0-9]/g, '');
      setIsVerified(false);
    }

    setForm({ ...form, [name]: formattedValue });
    
    if (name === 'email') setEmailError('');
  };

  const normalizeFullName = (name) => {
    return name.trim().replace(/\s+/g, ' ');
  };

  const validateForm = () => {
    const normalizedName = normalizeFullName(form.fullName);
    const nameParts = normalizedName.split(' ').filter(part => part.length > 0);
    
    if (nameParts.length !== 4) {
      alert(t('register.fullNameRules') || 'Please enter exactly 4 names (first, second, third, and last name)');
      return false;
    }
    
    const nameRegex = /^[a-z\u0600-\u06FF]+$/;
    for (let i = 0; i < nameParts.length; i++) {
      if (!nameRegex.test(nameParts[i])) {
        alert(t('register.fullNameRules') || 'Names must contain only letters (English or Arabic)');
        return false;
      }
    }

    if (form.phone.length !== 9) {
      alert(t('register.phoneRules'));
      return false;
    }

    if (form.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        alert(t('register.emailRules'));
        return false;
      }
      if (form.email.endsWith('.om')) {
         alert(t('register.emailTypo'));
         return false;
      }
    }

    return true;
  };

  const handleSendCode = async () => {
    if (!form.phone) {
      setMessage(t('register.enterPhoneFirst'));
      return;
    }
    
    if (form.phone.length !== 9) {
      setMessage(t('register.phoneRules'));
      return;
    }

    if (form.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        setMessage(t('register.emailRules'));
        return;
      }
      if (form.email.endsWith('.om')) {
         setMessage(t('register.emailTypo'));
         return;
      }
    }

    setSendingCode(true);
    setMessage('');
    try {
      const fullPhone = `+967${form.phone}`;
      await axios.post(`${config.API_BASE_URL}/Verification/send`, { PhoneNumber: fullPhone, Email: form.email });
      setShowVerifyModal(true);
      setMessage(t('register.codeSent'));
    } catch (error) {
      console.error(error);
      setMessage(t('register.failedToSendCode'));
    } finally {
      setSendingCode(false);
    }
  };

  const registerUser = async () => {
    try {
      const normalizedFullName = normalizeFullName(form.fullName);
      
      const payload = {
        FullName: normalizedFullName,
        Email: form.email,
        PasswordHash: form.password,
        Salt: "temp",
        Phone: `+967${form.phone}`,
        City: form.city,
        Address: form.address || '',
        Role: "User"
      };
      
      const res = await axios.post(`${config.API_BASE_URL}/UsersApi`, payload);
      
      if (res.data && res.data.Id) {
        try {
          await syncCurrencyAfterAuth(res.data.Id);
          console.log('✅ New user registered with YER currency');
        } catch (error) {
          console.error('⚠️ Could not set currency for new user:', error);
        }
        
        setMessage(t('register.accountCreated'));
        
        setTimeout(() => {
          setRedirect(true);
          cloneNotificationByTypeForUser('registration-success', res.data.Id);
        }, 1200);
      } else {
        setMessage(t('register.couldNotCreate'));
      }
    } catch (error) {
      console.error(error);
      setMessage(t('register.networkError'));
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setMessage(t('register.enterCode'));
      return;
    }
    setVerifying(true);
    try {
      const fullPhone = `+967${form.phone}`;
      await axios.post(`${config.API_BASE_URL}/Verification/verify`, { 
        PhoneNumber: fullPhone,
        Code: verificationCode 
      });
      setIsVerified(true);
      setShowVerifyModal(false);
      setMessage(t('register.phoneVerified'));
      await registerUser();
    } catch (error) {
      console.error(error);
      setMessage(t('register.invalidCode'));
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setEmailError('');
    setMessage('');

    if (!form.fullName || !form.password || !form.phone || !form.city) {
      setMessage(t('register.fillRequiredFields') || 'Please fill all required fields: Full Name, Password, Phone, and City');
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!isVerified) {
      handleSendCode();
      return;
    }

    registerUser();
  };

  if (redirect) {
    window.location.href = '/login';
    return null;
  }

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      direction: i18n.dir(),
      py: { xs: 2, sm: 0 }
    }}>
      <Card sx={{ 
        maxWidth: { xs: '100%', sm: 400 }, 
        width: '100%', 
        p: { xs: 2, sm: 3 }, // ✅ Reduced padding
        borderRadius: { xs: '16px', sm: '20px' }, // ✅ Smaller radius
        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`, // ✅ Lighter shadow
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: { xs: 1.5, sm: 2 }, // ✅ Reduced gap
        ...(i18n && i18n.dir && i18n.dir() === 'rtl' ? {
          '& input[name="fullName"], & input[name="email"], & input[name="address"], & input[name="password"]': {
            textAlign: 'right !important'
          }
        } : {})
      }}>
        <Avatar sx={{ 
          bgcolor: 'primary.main', 
          width: { xs: 48, sm: 56 }, // ✅ Smaller avatar
          height: { xs: 48, sm: 56 }, 
          mb: { xs: 1, sm: 1.5 } 
        }}>
          <PersonAddAltIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
        </Avatar>
        
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold', 
          mb: 0.5, 
          color: 'primary.main',
          fontSize: { xs: '1.1rem', sm: '1.5rem' } // ✅ Responsive font
        }}>
          {t('register.title')}
        </Typography>
        
        <Typography variant="body2" sx={{ 
          color: 'text.secondary', 
          mb: { xs: 1, sm: 1.5 },
          fontSize: { xs: '0.8rem', sm: '0.875rem' } // ✅ Smaller subtitle
        }}>
          {t('register.subtitle')}
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} sx={{ 
          width: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: { xs: 1.5, sm: 2 } // ✅ Reduced gap
        }}>
          <TextField
            name="fullName"
            dir={i18n && i18n.dir && i18n.dir() === 'rtl' ? 'rtl' : undefined}
            label={t('register.fullName')}
            variant="outlined"
            value={form.fullName}
            onChange={handleChange}
            required
            fullWidth
            size={isMobile ? "small" : "medium"} // ✅ Smaller on mobile
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } },
              '& .MuiInputBase-input': { 
                textAlign: (i18n && i18n.dir && i18n.dir() === 'rtl') ? 'right' : 'left',
                fontSize: { xs: '0.85rem', sm: '1rem' } // ✅ Smaller font
              }
            }}
            inputProps={ i18n && i18n.dir && i18n.dir() === 'rtl' ? { dir: 'rtl', style: { textAlign: 'right' } } : { dir: 'ltr' } }
            InputLabelProps={
              i18n && i18n.dir && i18n.dir() === 'rtl'
                ? {
                    sx: {
                      position: 'absolute',
                      right: 16,
                      left: 'auto',
                      textAlign: 'right',
                      transformOrigin: 'right top',
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      '&.MuiInputLabel-shrink': {
                        transformOrigin: 'left top',
                        left: 12,
                        right: 'auto',
                        transform: 'translate(0,-6px) scale(0.75)'
                      }
                    }
                  }
                : { sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }
            }
          />
          
          <TextField
            dir={i18n && i18n.dir && i18n.dir() === 'rtl' ? 'rtl' : undefined}
            name="email"
            label={`${t('register.email')} (${t('register.optional') || 'optional'})`}
            type="email"
            variant="outlined"
            value={form.email}
            onChange={handleChange}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } },
              '& .MuiInputBase-input': { 
                textAlign: (i18n && i18n.dir && i18n.dir() === 'rtl') ? 'right' : 'left',
                fontSize: { xs: '0.85rem', sm: '1rem' }
              }
            }}
            error={!!emailError}
            helperText={emailError || t('register.emailOptionalHelper') || 'Optional — used for receipts and password recovery.'}
            inputProps={ i18n && i18n.dir && i18n.dir() === 'rtl' ? { 'aria-describedby': 'email-helper-text', dir: 'rtl', style: { textAlign: 'right' } } : { 'aria-describedby': 'email-helper-text', dir: 'ltr' } }
            InputLabelProps={
              i18n && i18n.dir && i18n.dir() === 'rtl'
                ? {
                    sx: {
                      position: 'absolute',
                      right: 16,
                      left: 'auto',
                      textAlign: 'right',
                      transformOrigin: 'right top',
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      '&.MuiInputLabel-shrink': { transformOrigin: 'left top', left: 12, right: 'auto', transform: 'translate(0,-6px) scale(0.75)' }
                    }
                  }
                : { sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }
            }
          />
          
          <Box>
            <Typography variant="body2" sx={{ 
              mb: 0.5, 
              color: 'text.primary',
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}>
              {t('register.phone')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
              <Box sx={{ 
                bgcolor: 'background.paper', 
                border: '1px solid', 
                borderColor: 'divider', 
                px: 1, 
                borderRadius: { xs: '10px', sm: '12px' }, 
                display: 'flex', 
                alignItems: 'center', 
                height: { xs: '40px', sm: '48px' } // ✅ Smaller height
              }}>
                <Typography sx={{ 
                  fontWeight: 'bold', 
                  color: 'text.primary',
                  fontSize: { xs: '0.85rem', sm: '1rem' }
                }}>
                  +967
                </Typography>
              </Box>
              <TextField
                name="phone"
                variant="outlined"
                value={form.phone}
                onChange={handleChange}
                required
                fullWidth
                size={isMobile ? "small" : "medium"}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } }, 
                  flex: 1,
                  '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '1rem' } }
                }}
              />
            </Box>
          </Box>

          <TextField
            select
            name="city"
            variant="outlined"
            value={form.city}
            onChange={handleChange}
            required
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ 
              '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '1rem' } }
            }}
            SelectProps={{ native: true }}
            InputLabelProps={{ sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }}
          >
            <option value="">{t('register.selectCity')}</option>
            {cities.map(city => (
              <option key={city.Id || city.id} value={city.Name || city.name}>
                {city.Name || city.name}
              </option>
            ))}
          </TextField>
          
          <TextField
            dir={i18n && i18n.dir && i18n.dir() === 'rtl' ? 'rtl' : undefined}
            name="address"
            label={`${t('register.address')} (${t('register.optional') || 'optional'})`}
            variant="outlined"
            value={form.address}
            onChange={handleChange}
            fullWidth
            size={isMobile ? "small" : "medium"}
            helperText={t('register.addressOptionalHelper') || 'Optional — provide your delivery address if needed.'}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } },
              '& .MuiInputBase-input': { 
                textAlign: (i18n && i18n.dir && i18n.dir() === 'rtl') ? 'right' : 'left',
                fontSize: { xs: '0.85rem', sm: '1rem' }
              }
            }}
            inputProps={ i18n && i18n.dir && i18n.dir() === 'rtl' ? { dir: 'rtl', style: { textAlign: 'right' } } : { dir: 'ltr' } }
            InputLabelProps={
              i18n && i18n.dir && i18n.dir() === 'rtl'
                ? {
                    sx: {
                      position: 'absolute',
                      right: 16,
                      left: 'auto',
                      textAlign: 'right',
                      transformOrigin: 'right top',
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      '&.MuiInputLabel-shrink': { transformOrigin: 'left top', left: 12, right: 'auto', transform: 'translate(0,-6px) scale(0.75)' }
                    }
                  }
                : { sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }
            }
          />

          <TextField
            dir={i18n && i18n.dir && i18n.dir() === 'rtl' ? 'rtl' : undefined}
            name="password"
            label={t('register.password')}
            type={showPassword ? 'text' : 'password'}
            variant="outlined"
            value={form.password}
            onChange={handleChange}
            required
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: { xs: '10px', sm: '12px' } },
              '& .MuiInputBase-input': { 
                textAlign: (i18n && i18n.dir && i18n.dir() === 'rtl') ? 'right' : 'left',
                fontSize: { xs: '0.85rem', sm: '1rem' }
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                    onClick={() => setShowPassword(v => !v)}
                    edge="end"
                    size={isMobile ? "small" : "medium"}
                  >
                    {showPassword ? <VisibilityOff sx={{ fontSize: { xs: 18, sm: 24 } }} /> : <Visibility sx={{ fontSize: { xs: 18, sm: 24 } }} />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            inputProps={ i18n && i18n.dir && i18n.dir() === 'rtl' ? { dir: 'rtl', style: { textAlign: 'right' } } : { dir: 'ltr' } }
            InputLabelProps={
              i18n && i18n.dir && i18n.dir() === 'rtl'
                ? {
                    sx: {
                      position: 'absolute',
                      right: 16,
                      left: 'auto',
                      textAlign: 'right',
                      transformOrigin: 'right top',
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      '&.MuiInputLabel-shrink': { transformOrigin: 'left top', left: 12, right: 'auto', transform: 'translate(0,-6px) scale(0.75)' }
                    }
                  }
                : { sx: { fontSize: { xs: '0.85rem', sm: '1rem' } } }
            }
          />
          
          <Button 
            type="submit" 
            variant="contained" 
            size={isMobile ? "medium" : "large"}
            sx={{ 
              mt: { xs: 1, sm: 1.5 }, 
              py: { xs: 1.2, sm: 1.5 }, 
              fontWeight: 'bold', 
              fontSize: { xs: '0.9rem', sm: '1rem' }, 
              borderRadius: { xs: '10px', sm: '12px' }, 
              bgcolor: 'primary.main', 
              color: 'primary.contrastText', 
              '&:hover': { bgcolor: 'primary.dark' },
              '&:active': { transform: 'scale(0.98)' }
            }}
          >
            {t('register.signUp')}
          </Button>
        </Box>
        
        {message && (
          <Typography variant="body2" sx={{ 
            mt: 1, 
            color: (message.includes('success') || message.includes('بنجاح')) ? 'success.main' : 'error.main',
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}>
            {message}
          </Typography>
        )}
      </Card>

      <Dialog 
        open={showVerifyModal} 
        onClose={() => setShowVerifyModal(false)}
        PaperProps={{
          sx: {
            width: { xs: '90%', sm: 400 },
            borderRadius: { xs: 2, sm: 3 }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          {t('register.verifyPhone')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ 
            mb: 2,
            fontSize: { xs: '0.85rem', sm: '0.875rem' }
          }}>
            {t('register.enterCodeSentTo')} {form.phone}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('register.enterCode')}
            type="text"
            fullWidth
            variant="outlined"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            inputProps={{ maxLength: 6 }}
            size={isMobile ? "small" : "medium"}
            sx={{
              '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '1rem' } }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowVerifyModal(false)}
            size={isMobile ? "small" : "medium"}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleVerifyCode} 
            variant="contained" 
            disabled={verifying}
            size={isMobile ? "small" : "medium"}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'primary.contrastText', 
              '&:hover': { bgcolor: 'primary.dark' } 
            }}
          >
            {verifying ? t('common.loading') : t('register.verify')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Register;
